import { GlobalStyles } from '@spotify-internal/encore-creator-web';
import React from 'react';
import { connect } from 'react-redux';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { LandingPage } from '../LandingPage';
import { LOADING, UNAUTHORIZED, selectMeStatus, selectMe } from '../../store/selectors';
import { loadMe } from '../../store/me';
import * as Sentry from '@sentry/browser';
import { Helmet } from 'react-helmet';

import { ConnectedRoot } from '../../components/Root';
import { PublicHelpPageWithRouter } from '../HelpPage/PublicHelpPage';
import { LocationTrackerWithRouter } from './LocationTracker';
import { Me, ReduxState } from '../../types';
import { EmailUnsubscribe } from '../EmailUnsubscribe';
import { MeContext } from '../../context/me';
import { IntlProvider } from 'react-intl';

interface Props {
  me?: Me;
  status?: 'LOADING' | 'ERROR' | 'UNAUTHORIZED';
  loadMe(): void;
  initialRoute?: string;
}

class App extends React.Component<Props> {
  componentDidMount() {
    this.props.loadMe();
  }

  // istanbul ignore next
  componentDidCatch(error: any, info: any) {
    Sentry.withScope(scope => {
      scope.setTag('page_locale', navigator.language);
      scope.setExtras(info);
      Sentry.captureException(error);
    });
  }

  render() {
    if (this.props.status === LOADING) {
      return <span data-qa="app-loading" />;
    }

    // We can safely ignore this since Jest is doing some crazy mocking magic for our tests.
    // istanbul ignore next
    const initialEntries = this.props.initialRoute ? [this.props.initialRoute] : undefined;

    return (
      <>
        <GlobalStyles noFonts />
        <Helmet
          defaultTitle="Spotify for Podcasters"
          title={''}
          titleTemplate={'%s - Spotify for Podcasters'}
        />
        <MeContext.Provider value={this.props.me ? this.props.me : null}>
          <IntlProvider locale="en">
            {
              // @ts-ignore
              <BrowserRouter initialEntries={initialEntries}>
                <LocationTrackerWithRouter>
                  <Switch>
                    <Route path="/subscription/:subscriptionToken" component={EmailUnsubscribe} />
                    <Route exact path="/" component={LandingPage} />
                    {this.props.status === UNAUTHORIZED && (
                      <Route exact path="/faq" component={PublicHelpPageWithRouter} />
                    )}
                    <Route path="*" component={ConnectedRoot} />
                  </Switch>
                </LocationTrackerWithRouter>
              </BrowserRouter>
            }
          </IntlProvider>
        </MeContext.Provider>
      </>
    );
  }
}

function mapStateToProps(state: ReduxState) {
  return {
    status: selectMeStatus(state),
    me: selectMe(state),
  };
}

const actions = { loadMe };

export const ConnectedApp = connect(mapStateToProps, actions)(App);
